import { Box, IconButton, Typography, Theme } from '@mui/material';
import { useNavigate } from 'react-router';

import ErrorIcon from '@mui/icons-material/Error';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useParams, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import FormLinkButton from '../../../../components/buttons/FormLinkButton';
import usePageTitle from '../../../../hooks/usePageTitle';
import { useHasPermission } from '../../../../hooks/useHasPermission';
import { makeStyles } from 'tss-react/mui';
import { FieldHeadline } from '../../styled';
import CommonButton from '../../../../components/buttons/CommonButton';
import DraftDeductionsTable from './DraftDeductionsTable';
import DraftAttorneyFeeTable from './DraftAttorneyFeeTable';
import DraftPlaintiffTable from './DraftPlaintiffTable';
import EditDraftAttorneyFeePopup from '../EditDraftAttorneyFeePopup';
import EditDraftDeductionPopup from '../EditDraftDeductionPopup';
import EditDraftSettlementAmountsPopup from '../EditDraftSettlementAmountsPopup';
import Loader from '../../../../components/Loader';
import {
  getVendorPaymentMethodDetails,
  type VendorPaymentMethodDetails,
} from '../../../../api/vendorApi/vendorAccountsApi';
import {
  getDocumentSettlement,
  updateSettlementDraft,
  deleteDocumentSettlement,
  getDraft,
  saveDraft,
  updateDraft,
  deleteDraft,
  handleDraftError,
} from '../../../../api/paymentsApi/settlementsApi';
import useSnackBar from '../../../../hooks/useSnackBar';
import { AxiosError } from 'axios';
import { ErrorData } from '../../../../types';
import { SettlementDraft } from '../../../PaymentsView/types';
import { numberToUSDCurrency } from '../../../../helpers/numberToUSDCurrency';
import {
  getVendorDefaultPaymentAccountId,
  getVendorPaymentAccountOptions,
} from '../../../../helpers/vendorPaymentAccounts';
import Popup from '../../../../components/Popup';
import { POPUP_DESCRIPTIONS } from '../../../../constants/popupDescriptions';
import { StatusChip } from '../../../../components/StatusChip';
import theme from '../../../../theme';

const useStyles = makeStyles()((theme: Theme) => ({
  pageContainer: {
    display: 'flex',
    width: '100%',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
    padding: '0 14px',
    boxSizing: 'border-box',
  },
  topRowContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    gap: '24px',

    '@media (max-width: 1200px)': {
      flexDirection: 'column',
    },
  },
  leftColumnsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    flex: 1,
    maxWidth: '600px',
  },
  formLeftColumnContainer: {
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.primary.light,
    borderRadius: '16px',
    padding: '22px',
    flexShrink: 1,
    minWidth: 0,

    '@media (max-width: 1200px)': {
      flexShrink: 'inherit',
      minWidth: 'inherit',
    },

    '@media (max-width: 650px)': {
      padding: '16px',
    },
  },
  formLeftDeductionContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    background: theme.palette.primary.light,
    borderRadius: '16px',
    padding: '20px',
    boxSizing: 'border-box',

    '@media (max-width: 1200px)': {
      flexShrink: 'inherit',
      minWidth: 'inherit',
    },

    '@media (max-width: 650px)': {
      padding: '16px',
    },
  },
  formCheckDetailsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  formActionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '200px',
    flexShrink: 0,

    '@media (max-width: 1200px)': {
      flexBasis: 'auto',
      maxWidth: '250px',
    },
  },
  warningBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    backgroundColor: '#ADDC7B14',
    border: `1px solid ${theme.palette.warning.main}`,
    borderRadius: '8px',
    padding: '16px',
    width: '100%',
    boxSizing: 'border-box',
  },
  warningIconWrapper: {
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '2px',
    color: theme.palette.warning.main,
  },
  warningHeading: {
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'Inter',
    color: theme.palette.text.primary,
    marginBottom: '4px',
  },
  warningMessage: {
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: 'Inter',
    color: theme.palette.text.secondary,
  },
  feesSummaryWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  feesSummaryHeadline: {
    fontFamily: 'Inter',
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '24px',
    paddingRight: '10px',
    paddingBottom: '10px',
  },
  feesSummaryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
    alignItems: 'center',
    color: theme.palette.common.white,

    button: {
      p: {
        fontFamily: 'Inter',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
      },
    },
  },
  feesSummaryHeaderPrintBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
  },
  feesSummaryContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    background: theme.palette.primary.light,
    minHeight: '400px',
    borderRadius: '16px',

    '@media (max-width: 650px)': {
      padding: '16px',
    },
  },
  feesSummaryTotalGrossHeadline: {
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
    padding: '10px',
    color: theme.palette.common.white,

    p: {
      fontFamily: 'Inter',
      fontSize: '18px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: 'normal',
    },
  },
  feesSummarySectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '20px',
  },
  feesSummarySectionHeadline: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px',
    color: theme.palette.common.white,

    p: {
      fontFamily: 'Inter',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: 'normal',
    },
  },
  feesSummarySectionInnerHeadline: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 20px 10px 34px',
    color: theme.palette.common.white,

    p: {
      fontFamily: 'Inter',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 'normal',
    },
  },
  feesSummaryBlockDivider: {
    height: '1px',
    background: theme.palette.secondary.dark,
    marginTop: '20px',
    marginBottom: '20px',
    marginLeft: '10px',
  },
  firmNameText: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '16.8px',
    color: theme.palette.common.white,
    flexBasis: 'auto',
    overflow: 'hidden',
    minWidth: 0,
    textOverflow: 'ellipsis',
    maxWidth: '130px',
    whiteSpace: 'nowrap',
  },
  printButton: {
    color: 'white',
    padding: 0,

    '&.Mui-disabled': {
      color: theme.palette.text.disabled,
    },
  },
  secondaryFirmsInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  noCheckTextList: {
    listStyleType: 'disc',
    padding: 0,
    paddingLeft: '30px',
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Roboto',
    fontSize: '16px',
    gap: '3px',
    color: theme.palette.common.white,
  },
  settlementSummaryBox: {
    width: '100%',
    //  marginTop: '16px',
    // border: `1px solid ${theme.palette.secondary.dark}`,
    // borderRadius: '8px',
    backgroundColor: theme.palette.primary.light,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '16px',
    boxSizing: 'border-box',
    fontFamily: 'Inter',
    color: '#FFFFFF',
  },
  settlementSummaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  settlementSummaryRowNet: {
    display: 'grid',
    gridTemplateColumns: 'minmax(120px, 1fr) auto',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    width: '100%',
    marginTop: '13px',
    padding: '12px',
    border: `1px solid ${theme.palette.secondary.light}`,
    borderRadius: '4px',
    boxSizing: 'border-box',
  },
  settlementSummaryNetDetails: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
    minWidth: 0,
  },
  settlementSummaryNetPip: {
    textAlign: 'right',
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: 'Inter',
    color: '#FFFFFFB3',
  },
  settlementSummaryAmountActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  settlementSummaryAmount: {
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  settlementSummaryAmountValue: {
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  settlementSummaryKeyGross: {
    fontSize: '14px',
    fontFamily: 'Inter',
    fontWeight: 500,
    color: '#FFFFFF',
  },
  settlementSummaryKeyDeduction: {
    fontSize: '14px',
    fontFamily: 'Inter',
    fontWeight: 500,
    color: '#FFFFFF',
    marginLeft: '8px',
  },
  settlementSummaryKeyIndented: {
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Inter',
    color: '#FFFFFFB3',
    marginLeft: '25px',
  },
  settlementSummaryKeyBold: {
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'Inter',
    color: '#FFFFFF',
  },
  settlementSummaryValue: {
    fontSize: '14px',
    fontWeight: 200,
    fontFamily: 'Inter',
    color: theme.palette.common.white,
  },
  settlementSummaryValueIndented: {
    fontSize: '14px',
    fontWeight: 200,
    fontFamily: 'Inter',
    color: '#FFFFFFB3',
  },
  noDetailsTextBlock: {
    display: 'flex',
    flexDirection: 'column',
    height: '452px',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#35394B',
    borderRadius: '16px',
    padding: '32px',
    gap: '24px',
  },
  noDetailsText: {
    fontFamily: 'Poppins',
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '120%',
    textAlign: 'center',
    color: theme.palette.common.white,
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '46px',
  },
}));

const SettlementDraftView = () => {
  const { settlementId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // The state passed from navigate (res object)
  const settlementDraftState = location.state;
  const { setAlert } = useSnackBar();
  const canViewCases = useHasPermission('Cases', 'View');
  const canViewVendors = useHasPermission('Vendors', 'View');
  const canCreateVendors = useHasPermission('Vendors', 'Create');

  const { setPageTitle } = usePageTitle();
  const { classes } = useStyles();

  // Parse URL query parameters for draft loading
  const queryParams = new URLSearchParams(location.search);
  const draftIdParam = queryParams.get('draftId');

  const [isEditAttorneyFeePopupOpen, setIsEditAttorneyFeePopupOpen] = useState<boolean>(false);
  const [isEditAmountsPopupOpen, setIsEditAmountsPopupOpen] = useState<boolean>(false);
  const [selectedAttorneyFeeIndex, setSelectedAttorneyFeeIndex] = useState<number | null>(null);
  const [isEditDeductionPopupOpen, setIsEditDeductionPopupOpen] = useState<boolean>(false);
  const [selectedDeductionIndex, setSelectedDeductionIndex] = useState<number | null>(null);

  const [fetchSettlement, setFetchSettlement] = useState<SettlementDraft | null>(
    settlementDraftState,
  );
  const [currentDraftId, setCurrentDraftId] = useState<number | null>(
    draftIdParam ? Number(draftIdParam) : null,
  );

  // Track initial draft state for change detection
  const [initialDraftState, setInitialDraftState] = useState<string | null>(null);

  /**
   * Remove accounting sync fields from settlement data for change detection.
   * Accounting sync happens outside draft scope and is reconciled on load,
   * so changes to accounting fields should not trigger "draft has changes" state.
   */
  const removeAccountingFields = (
    settlement: SettlementDraft | null,
  ): Partial<SettlementDraft> | null => {
    if (!settlement) return null;

    const cleaned = { ...settlement };

    // Remove accounting from deductions
    if (cleaned.deductions) {
      cleaned.deductions = cleaned.deductions.map((d) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { accounting: _accounting, ...rest } = d;
        return rest;
      });
    }

    // Remove accounting from attorney fees
    if (cleaned.attorneysFees) {
      const fees = { ...cleaned.attorneysFees };
      if (fees.leadFirm) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { accounting: _accounting, ...rest } = fees.leadFirm;
        fees.leadFirm = rest;
      }
      if (fees.secondaryFirms) {
        fees.secondaryFirms = fees.secondaryFirms.map((f) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { accounting: _accounting, ...rest } = f;
          return rest;
        });
      }
      cleaned.attorneysFees = fees;
    }

    // Remove accounting from net settlement
    if (cleaned.netSettlement) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { accounting: _accounting, ...rest } = cleaned.netSettlement;
      cleaned.netSettlement = rest;
    }

    return cleaned;
  };

  // Check if draft has changes (excluding accounting sync fields)
  const hasChanges = useMemo(() => {
    if (!fetchSettlement || !initialDraftState || !currentDraftId) return false;
    const currentStateWithoutAccounting = JSON.stringify(removeAccountingFields(fetchSettlement));
    return currentStateWithoutAccounting !== initialDraftState;
  }, [fetchSettlement, initialDraftState, currentDraftId]);

  // Load draft from API when draftId is in URL
  useEffect(() => {
    if (draftIdParam && !settlementDraftState) {
      setIsLoading(true);
      getDraft({ draftId: Number(draftIdParam) })
        .then((res) => {
          if (res.data) {
            // Transform MemoDraftResponse to SettlementDraft format
            // Preserve all fields exactly as stored to maintain vendor matching and accounting sync state
            const draftData = res.data;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const settlementDraft: any = {
              case: draftData.case || { id: 0, name: '', referenceId: '' },
              client: draftData.client || { id: 0, firstName: '', middleName: null, lastName: '' },
              defendant: draftData.defendant || { id: 0, name: '' },
              settledAmount: draftData.settledAmount ?? 0,
              pipAmount: draftData.pipAmount ?? 0,
              settlementType:
                draftData.settlementType ??
                ((draftData.pipAmount ?? 0) > 0 ? 'STANDARD_PIP' : 'STANDARD'),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              attorneysFees: draftData.attorneysFees,
              // Preserve complete deduction structure including vendor matching state
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              deductions: (draftData.deductions || []).map((d: any) => ({
                id: d.id ?? 0,
                type: d.type,
                amount: d.amount,
                vendorParticipationId: d.vendorParticipationId,
                vendorName: d.vendorName, // Preserve null for unmatched vendors
                vendorPaymentAccountId: d.vendorPaymentAccountId ?? null,
                vendorPaymentAccountName: d.paymentAccountName ?? null,
                vendorSuggestions: d.vendorSuggestions || [],
                description: d.description,
                memo: d.memo,
                paymentMethod: d.paymentMethod,
                disposition: d.disposition,
                matchingMode: d.matchingMode, // Preserve null/fuzzy/exact state
                originalText: d.originalText,
                provisioning: d.provisioning,
                accounting: d.accounting, // Preserve accounting sync status
              })),
              fileHash: draftData.fileHash ?? '',
              filename: draftData.filename ?? '',
              docpath: draftData.docpath ?? '',
              // Preserve net settlement with accounting info
              netSettlement: draftData.netSettlement,
              isAccountingIntegrated: draftData.isAccountingIntegrated ?? false,
              warningMessages: draftData.warningMessages || [],
            };
            setFetchSettlement(settlementDraft);
            setCurrentDraftId(draftData.draftId);
            // Set initial state for change detection (excluding accounting fields)
            setInitialDraftState(JSON.stringify(removeAccountingFields(settlementDraft)));
          }
        })
        .catch((error) => {
          const axiosError = error as AxiosError<ErrorData>;
          setAlert({
            message: handleDraftError(axiosError),
            type: 'error',
          });
          // Navigate back to create settlement on error
          navigate('/payments/create-settlement');
        })
        .finally(() => {
          // Draft loading complete
          setIsLoading(false);
        });
    }
  }, [draftIdParam, settlementDraftState]);

  // Clear browser history state on mount so a page refresh won't restore the draft data
  useEffect(() => {
    window.history.replaceState({}, document.title);
  }, []);

  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState<boolean>(false);
  const handleOpenDeletePopup = () => setIsDeletePopupOpen(true);
  const handleCloseDeletePopup = () => setIsDeletePopupOpen(false);

  const [isDeleteDraftPopupOpen, setIsDeleteDraftPopupOpen] = useState<boolean>(false);
  const handleOpenDeleteDraftPopup = () => setIsDeleteDraftPopupOpen(true);
  const handleCloseDeleteDraftPopup = () => setIsDeleteDraftPopupOpen(false);

  const [isDeleteDeductionPopupOpen, setIsDeleteDeductionPopupOpen] = useState<boolean>(false);
  const [deductionToDelete, setDeductionToDelete] = useState<number | null>(null);
  const handleOpenDeleteDeductionPopup = () => setIsDeleteDeductionPopupOpen(true);
  const handleCloseDeleteDeductionPopup = () => {
    setIsDeleteDeductionPopupOpen(false);
    setDeductionToDelete(null);
  };

  const [isSaveSettlementPopupOpen, setIsSaveSettlementPopupOpen] = useState<boolean>(false);
  const handleOpenSaveSettlementPopup = () => setIsSaveSettlementPopupOpen(true);
  const handleCloseSaveSettlementPopup = () => setIsSaveSettlementPopupOpen(false);

  const shouldLoadDraft = Boolean(draftIdParam && !settlementDraftState);
  const [isLoading, setIsLoading] = useState<boolean>(shouldLoadDraft);

  const deductionFields = fetchSettlement?.deductions || [];

  const mappedDeductions = useMemo(
    () =>
      deductionFields.map((deduction) => ({
        ...deduction,
        // Only map Advance -> Expense Reimbursement; keep SERVICE_PROVIDER and Lien as raw API values
        type: deduction.type === 'Advance' ? 'Expense Reimbursement' : deduction.type,
        // amount: numberToUSDCurrency(deduction.amount),
        amount: deduction.amount,
        vendorName: deduction.vendorName
          ? {
              id: deduction.vendorParticipationId ? String(deduction.vendorParticipationId) : '',
              label: deduction.vendorName,
              accounting: deduction.accounting ?? undefined,
            }
          : { id: '', label: '' },
        vendorParticipationId: deduction.vendorParticipationId
          ? String(deduction.vendorParticipationId)
          : null,
        matchingMode: deduction.matchingMode ?? null,
        description: deduction.description ?? '',
        accounting: deduction.accounting ?? null,
        vendorPaymentAccountId: deduction.vendorPaymentAccountId ?? null,
        vendorPaymentAccountName: deduction.vendorPaymentAccountName ?? null,
      })),
    [deductionFields],
  );

  const attorneyFeesFields = fetchSettlement?.attorneysFees;
  const mappedAttorneysFirms = attorneyFeesFields
    ? [
        {
          ...attorneyFeesFields.leadFirm,
          // fee: numberToUSDCurrency(attorneyFeesFields.leadFirm.fee),
          fee: attorneyFeesFields.leadFirm.fee,
        },
        ...(attorneyFeesFields.secondaryFirms?.map((firm) => ({
          ...firm,
          // fee: numberToUSDCurrency(firm.fee),
          fee: firm.fee,
        })) || []),
      ]
    : [];

  useEffect(() => {
    setPageTitle && setPageTitle('Settlement Draft');
    return () => setPageTitle && setPageTitle('');
  }, []);
  // Track if any mappedDeductions have matchingMode null or 'fuzzy' or unsynced accounting
  const [hasFuzzyOrNullMatchingMode, setHasFuzzyOrNullMatchingMode] = useState(false);
  // Track if any vendor deduction has a missing payment account or an unprovisioned payment account/address
  const [hasVendorProvisioningIssue, setHasVendorProvisioningIssue] = useState(false);
  useEffect(() => {
    // Check deductions
    const hasDeductionIssues = mappedDeductions.some((d) => {
      const isLienOrServiceProvider = d.type === 'Lien' || d.type === 'SERVICE_PROVIDER';
      if (!isLienOrServiceProvider) return false;

      // Check for fuzzy/null matching mode (for Liens)
      const hasBadMatchingMode =
        d.type === 'Lien' && (d.matchingMode === null || d.matchingMode === 'fuzzy');

      // Check for unsynced accounting (for both Liens and Service Providers)
      const hasUnsyncedAccounting = d.accounting && !d.accounting.isSynced;

      return hasBadMatchingMode || hasUnsyncedAccounting;
    });

    // Check for missing/unprovisioned vendor payment account or address (Liens and Service Providers with Pay intent)
    const hasProvisioningIssue = mappedDeductions.some((d) => {
      const isLienOrServiceProvider = d.type === 'Lien' || d.type === 'SERVICE_PROVIDER';
      if (!isLienOrServiceProvider) return false;

      const isPayDisposition = ['PAY', 'Pay'].includes(d.disposition);
      const normalizedPaymentMethod = (d.paymentMethod || '').toUpperCase();
      const requiresPaymentAccount =
        isPayDisposition && (normalizedPaymentMethod === 'ACH' || normalizedPaymentMethod === 'CHECK');

      if (!requiresPaymentAccount) return false;

      const provisioningKey = normalizedPaymentMethod === 'ACH' ? 'ach' : 'check';

      return !d.vendorPaymentAccountId || d.provisioning?.[provisioningKey]?.isProvisioned !== true;
    });

    // Check attorney fees (leadFirm and secondaryFirms)
    const hasAttorneyFeesUnsynced =
      attorneyFeesFields &&
      ((attorneyFeesFields.leadFirm?.accounting &&
        !attorneyFeesFields.leadFirm.accounting.isSynced) ||
        attorneyFeesFields.secondaryFirms?.some(
          (firm) => firm.accounting && !firm.accounting.isSynced,
        ));

    // Check plaintiff (net settlement) sync status
    const hasPlaintiffUnsynced =
      fetchSettlement?.netSettlement?.accounting &&
      !fetchSettlement.netSettlement.accounting.isSynced;
    setHasFuzzyOrNullMatchingMode(
      (hasDeductionIssues || hasAttorneyFeesUnsynced || hasPlaintiffUnsynced) ?? false,
    );
    setHasVendorProvisioningIssue(hasProvisioningIssue);
  }, [mappedDeductions, attorneyFeesFields, fetchSettlement?.netSettlement]);

  // Auto-save draft when changes are made (with debounce)
  useEffect(() => {
    // Only auto-save if we have a draft and there are changes
    if (!currentDraftId || !hasChanges || !fetchSettlement) return;

    // Debounce the auto-save to avoid too many API calls
    const timeoutId = setTimeout(() => {
      handleUpdateDraft();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [fetchSettlement, currentDraftId, hasChanges]);

  const handleDeleteDocument = async () => {
    const documentKey = fetchSettlement?.docpath;
    if (!documentKey) {
      setAlert((prev) => ({
        ...prev,
        message: 'Document path not found.',
        type: 'error',
      }));
      return;
    }

    // const confirmed = window.confirm('Are you sure you want to delete this document? This action cannot be undone.');
    // if (!confirmed) {
    //     return;
    // }

    try {
      const encodedDocumentKey = encodeURIComponent(documentKey);
      const response = await deleteDocumentSettlement(encodedDocumentKey);
      if (response.status === 200) {
        setAlert((prev) => ({
          ...prev,
          message: response.data?.message || 'Document deleted successfully.',
          type: 'success',
        }));
        // Clear the location state before navigating
        window.history.replaceState({}, document.title);
        // Navigate to create settlement form container page
        navigate('/payments/create-settlement/');
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((prev) => ({
        ...prev,
        message: axiosError.response?.data?.message || 'Document deletion error',
        type: 'error',
      }));
      handleCloseDeletePopup();
    }
  };

  const handleDownloadFiles = async () => {
    const documentKey = fetchSettlement?.docpath;
    if (!documentKey) {
      setAlert((prev) => ({
        ...prev,
        message: 'Document not found.',
        type: 'info',
      }));
      return;
    }

    try {
      const encodedDocumentKey = encodeURIComponent(documentKey);
      const response = await getDocumentSettlement(encodedDocumentKey);
      if (response.data?.downloadUrl) {
        const fileUrl = response.data.downloadUrl;
        // Trigger download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = ''; // Browser will use the filename from the URL
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setAlert((prev) => ({
          ...prev,
          message: response.data?.message || 'No file found to download.',
          type: 'info',
        }));
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((prev) => ({
        ...prev,
        message: axiosError.response?.data?.message || 'File download error',
        type: 'error',
      }));
    }
  };

  const handleDeductionMenuOptionClick = (option: string, index: number) => {
    if (option === 'Edit') {
      setSelectedDeductionIndex(index);
      setIsEditDeductionPopupOpen(true);
    }
    if (option === 'Delete') {
      setDeductionToDelete(index);
      handleOpenDeleteDeductionPopup();
    }
  };

  const handleDeleteDeduction = async () => {
    if (deductionToDelete === null) return;

    setFetchSettlement((prev) => {
      if (!prev) return prev;
      const updatedDeductions = prev.deductions.filter(
        (deduction) => deduction.id !== deductionToDelete,
      );
      return { ...prev, deductions: updatedDeductions };
    });

    handleCloseDeleteDeductionPopup();
    // Auto-save will trigger via useEffect
  };

  const getFormattedAccountOptions = (details: VendorPaymentMethodDetails, method: string) => {
    return getVendorPaymentAccountOptions(details, method).map((item) => {
      const subtitleWithoutAccountType =
        item.paymentMethod === 'ACH' ? item.subtitle.split(' ').slice(-1).join(' ') : item.subtitle;

      return {
        id: item.id,
        label: subtitleWithoutAccountType ? `${item.label} - ${subtitleWithoutAccountType}` : item.label,
      };
    });
  };

  const handleConfirmClick = (id: number) => {
    const applyConfirm = async () => {
      const deduction = fetchSettlement?.deductions?.find((item) => item.id === id);
      if (!deduction) return;

      const suggestedVendor = deduction.vendorSuggestions?.[0];
      const nextVendorParticipationId = suggestedVendor?.id ?? deduction.vendorParticipationId ?? null;
      const nextVendorName = suggestedVendor?.vendorName ?? deduction.vendorName ?? null;
      const nextPaymentMethod = suggestedVendor?.paymentMethod || deduction.paymentMethod || '';
      const normalizedNextPaymentMethod = nextPaymentMethod.toUpperCase();
      const requiresPaymentAccount =
        normalizedNextPaymentMethod === 'ACH' || normalizedNextPaymentMethod === 'CHECK';

      let nextVendorPaymentAccountId = deduction.vendorPaymentAccountId ?? null;
      let nextVendorPaymentAccountName = deduction.vendorPaymentAccountName ?? null;
      let nextProvisioning =
        suggestedVendor?.provisioning ||
        deduction.provisioning || {
          ach: { isProvisioned: false },
          check: { isProvisioned: false },
        };

      if (requiresPaymentAccount) {
        // Reset account/provisioning to avoid keeping stale values from previously selected vendors.
        nextVendorPaymentAccountId = null;
        nextVendorPaymentAccountName = null;

        nextProvisioning = {
          ...nextProvisioning,
          ach:
            normalizedNextPaymentMethod === 'ACH'
              ? { isProvisioned: false }
              : nextProvisioning.ach,
          check:
            normalizedNextPaymentMethod === 'CHECK'
              ? { isProvisioned: false }
              : nextProvisioning.check,
        };
      }

      if (
        nextVendorParticipationId &&
        (normalizedNextPaymentMethod === 'ACH' || normalizedNextPaymentMethod === 'CHECK')
      ) {
        try {
          const response = await getVendorPaymentMethodDetails(Number(nextVendorParticipationId));
          const vendorDetails = response.data;
          const accountOptions = getFormattedAccountOptions(vendorDetails, normalizedNextPaymentMethod);

          const defaultAccountId = getVendorDefaultPaymentAccountId(
            vendorDetails,
            normalizedNextPaymentMethod,
          );
          const defaultAccountOption = accountOptions.find((option) => option.id === defaultAccountId);

          if (defaultAccountOption) {
            nextVendorPaymentAccountId = Number(defaultAccountOption.id);
            nextVendorPaymentAccountName = defaultAccountOption.label;
          }

          const selectedAccountId = nextVendorPaymentAccountId
            ? String(nextVendorPaymentAccountId)
            : '';

          if (normalizedNextPaymentMethod === 'ACH') {
            const selectedAchAccount = vendorDetails.achAccounts?.find(
              (account) => String(account.id) === selectedAccountId,
            );

            nextProvisioning = {
              ...nextProvisioning,
              ach: {
                isProvisioned: selectedAchAccount?.provisionStatus === 'Completed',
              },
            };
          }

          if (normalizedNextPaymentMethod === 'CHECK') {
            const selectedCheckAccount = vendorDetails.checkAccounts?.find(
              (account) => String(account.id) === selectedAccountId,
            );

            nextProvisioning = {
              ...nextProvisioning,
              check: {
                isProvisioned: selectedCheckAccount?.provisionStatus === 'Completed',
              },
            };
          }
        } catch {
          // Keep current payment account values if vendor payment details cannot be loaded.
        }
      }

      setFetchSettlement((prev) => {
        if (!prev) return prev;

        const updatedDeductions = prev.deductions.map((item) =>
          item.id === id
            ? {
                ...item,
                matchingMode: 'exact',
                vendorParticipationId: nextVendorParticipationId,
                vendorName: nextVendorName,
                paymentMethod: nextPaymentMethod,
                vendorPaymentAccountId: nextVendorPaymentAccountId,
                vendorPaymentAccountName: nextVendorPaymentAccountName,
                accounting: suggestedVendor?.accounting || item.accounting,
                provisioning: nextProvisioning,
              }
            : item,
        );

        return { ...prev, deductions: updatedDeductions };
      });
    };

    void applyConfirm();
    // Auto-save will trigger via useEffect
  };

  const handleAttorneyFeeMenuOptionClick = (option: string, index: number) => {
    if (option === 'Edit') {
      setSelectedAttorneyFeeIndex(index);
      setIsEditAttorneyFeePopupOpen(true);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getDeductionUpdatedData = (data: any) => {
    if (!data || !data.id) return;
    setFetchSettlement((prev) => {
      if (!prev) return prev;
      const updatedDeductions = prev.deductions.map((deduction) =>
        deduction.id === data.id
          ? {
              ...deduction,
              ...data,
            }
          : deduction,
      );
      return { ...prev, deductions: updatedDeductions };
    });
  };

  const calculateAttorneysFees = () => {
    if (!fetchSettlement?.attorneysFees) return 0;
    const leadFee = fetchSettlement.attorneysFees.leadFirm?.fee || 0;
    const secondaryFees =
      fetchSettlement.attorneysFees.secondaryFirms?.reduce(
        (sum, firm) => sum + (firm.fee || 0),
        0,
      ) || 0;
    return leadFee + secondaryFees;
  };

  const calculateLiens = () => {
    const liens = fetchSettlement?.deductions?.filter((d) => d.type === 'Lien') || [];
    return liens.reduce((sum, lien) => {
      const amount =
        typeof lien.amount === 'string' ? parseFloat(lien.amount) || 0 : lien.amount || 0;
      return sum + amount;
    }, 0);
  };

  const calculateExpenses = () => {
    const expenses = fetchSettlement?.deductions?.filter((d) => d.type === 'Advance') || [];
    return expenses.reduce((sum, expense) => {
      const amount =
        typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0;
      return sum + amount;
    }, 0);
  };

  const calculateServiceProviders = () => {
    const serviceProviders =
      fetchSettlement?.deductions?.filter((d) => d.type === 'SERVICE_PROVIDER') || [];
    return serviceProviders.reduce((sum, serviceProvider) => {
      const amount =
        typeof serviceProvider.amount === 'string'
          ? parseFloat(serviceProvider.amount) || 0
          : serviceProvider.amount || 0;
      return sum + amount;
    }, 0);
  };

  const calculateTotalDeductions = () => {
    return (
      fetchSettlement?.deductions?.reduce((sum, deduction) => {
        const amount =
          typeof deduction.amount === 'string'
            ? parseFloat(deduction.amount) || 0
            : deduction.amount || 0;
        return sum + amount;
      }, 0) || 0
    );
  };

  const calculateNetToPlaintiff = () => {
    const settledAmount = fetchSettlement?.settledAmount || 0;
    const pipAmount = fetchSettlement?.pipAmount || 0;
    const attorneyFees = calculateAttorneysFees();
    const totalDeductions = calculateTotalDeductions();
    const netAmountIncludingPip = settledAmount - attorneyFees - totalDeductions + pipAmount;
    if (fetchSettlement?.netSettlement) {
      fetchSettlement.netSettlement.amount = netAmountIncludingPip;
    }
    return netAmountIncludingPip;
  };

  const handleApplyDraftAmounts = (amounts: { settledAmount: number; pipAmount: number }) => {
    setFetchSettlement((prev) => {
      if (!prev) return prev;
      const totalDeductions = (prev.deductions || []).reduce(
        (sum, deduction) => sum + Number(deduction.amount || 0),
        0,
      );
      const attorneyFees = prev.attorneysFees?.total || 0;
      const netAmount =
        amounts.settledAmount + amounts.pipAmount - totalDeductions - attorneyFees;
      return {
        ...prev,
        settledAmount: amounts.settledAmount,
        pipAmount: amounts.pipAmount,
        netSettlement: prev.netSettlement
          ? { ...prev.netSettlement, amount: netAmount }
          : prev.netSettlement,
      };
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getAttorneyFeeUpdatedData = (data: any) => {
    if (!data || !data.id) return;
    setFetchSettlement((prev) => {
      if (!prev) return prev;
      if (!prev.attorneysFees) return prev;
      let updatedAttorneysFees = { ...prev.attorneysFees };
      // Check if updating leadFirm
      if (updatedAttorneysFees.leadFirm.id === data.id) {
        updatedAttorneysFees = {
          ...updatedAttorneysFees,
          leadFirm: { ...updatedAttorneysFees.leadFirm, ...data },
        };
      } else {
        // Otherwise, update in secondaryFirms
        updatedAttorneysFees = {
          ...updatedAttorneysFees,
          secondaryFirms: updatedAttorneysFees.secondaryFirms.map((firm) =>
            firm.id === data.id ? { ...firm, ...data } : firm,
          ),
        };
      }
      return { ...prev, attorneysFees: updatedAttorneysFees };
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleAttorneyFeeFieldUpdate = (updatedField: any) => {
    if (!updatedField || !updatedField.id) return;
    setFetchSettlement((prev) => {
      if (!prev) return prev;
      if (!prev.attorneysFees) return prev;
      let updatedAttorneysFees = { ...prev.attorneysFees };
      // Check if updating leadFirm
      if (updatedAttorneysFees.leadFirm.id === updatedField.id) {
        updatedAttorneysFees = {
          ...updatedAttorneysFees,
          leadFirm: updatedField,
        };
      } else {
        // Otherwise, update in secondaryFirms
        updatedAttorneysFees = {
          ...updatedAttorneysFees,
          secondaryFirms: updatedAttorneysFees.secondaryFirms.map((firm) =>
            firm.id === updatedField.id ? updatedField : firm,
          ),
        };
      }
      return { ...prev, attorneysFees: updatedAttorneysFees };
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePlaintiffUpdate = (updatedNetSettlement: any) => {
    if (!updatedNetSettlement) return;
    setFetchSettlement((prev) => {
      if (!prev) return prev;
      return { ...prev, netSettlement: updatedNetSettlement };
    });
  };

  const onSubmitForm = async () => {
    if (!fetchSettlement) return;

    // If we have a current draft ID, show confirmation popup
    if (currentDraftId) {
      handleOpenSaveSettlementPopup();
      return;
    }

    // Otherwise, proceed with settlement creation
    await createSettlement();
  };

  const createSettlement = async () => {
    if (!fetchSettlement) return;

    // Validate required fields for settlement creation
    if (!fetchSettlement.case || !fetchSettlement.defendant) {
      setAlert({
        message:
          'Missing required settlement information. Please ensure case and defendant are properly set.',
        type: 'error',
      });
      handleCloseSaveSettlementPopup();
      return;
    }

    const deductions = (fetchSettlement.deductions || []).map((deduction) => ({
      type: deduction.type,
      amount: deduction.amount,
      vendorParticipationId: deduction.vendorParticipationId,
      vendorPaymentAccountId: deduction.vendorPaymentAccountId ?? null,
      description: deduction.description || null,
      memo: deduction.memo,
      paymentMethod: deduction.type === 'Advance' ? 'V' : deduction.paymentMethod,
      disposition: deduction.disposition,
    }));

    const attorneysFees = fetchSettlement.attorneysFees
      ? { total: fetchSettlement.attorneysFees.total }
      : null;

    const payload = {
      caseId: fetchSettlement.case.id,
      defendant: { id: fetchSettlement.defendant.id },
      settledAmount: fetchSettlement.settledAmount,
      pipAmount: fetchSettlement.pipAmount || 0,
      settlementType: fetchSettlement.settlementType || 'STANDARD',
      deductions,
      attorneysFees,
      filename: fetchSettlement.filename || '',
      docpath: fetchSettlement.docpath || '',
      fileHash: fetchSettlement.fileHash || '',
      ...(currentDraftId !== null ? { draftId: currentDraftId } : {}),
    };

    try {
      const response = await updateSettlementDraft(payload);
      if (response && response.data) {
        const settlementId = response.data.settlementId;

        // If there's an active draft, it will be automatically deactivated by the backend
        // via deactivateDraftOnSettlementCreation service method

        // Clear the location state before navigating
        window.history.replaceState({}, document.title);
        navigate(`/payments/settlement/${settlementId}/home`);
        setAlert({
          message: 'Settlement created successfully.',
          type: 'success',
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert({
        message: axiosError.response?.data?.message || 'Settlement creation error',
        type: 'error',
      });
    }
    handleCloseSaveSettlementPopup();
  };

  const handleSaveDraft = async () => {
    if (!fetchSettlement) return;

    // Validate required fields
    if (!fetchSettlement.case || !fetchSettlement.client || !fetchSettlement.defendant) {
      setAlert({
        message:
          'Missing required settlement information. Please ensure case, client, and defendant are properly set.',
        type: 'error',
      });
      return;
    }

    // Prepare draft data - preserve ALL fields for vendor matching, accounting sync, etc.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const draftData: any = {
      case: fetchSettlement.case,
      client: fetchSettlement.client,
      defendant: fetchSettlement.defendant,
      settledAmount: fetchSettlement.settledAmount || 0,
      pipAmount: fetchSettlement.pipAmount || 0,
      settlementType: fetchSettlement.settlementType || 'STANDARD',
      status: fetchSettlement.status || 'Draft',
      // Preserve complete deduction structure including vendor matching state and accounting
      deductions: (fetchSettlement.deductions || []).map((d) => ({
        id: d.id,
        type: d.type,
        amount: d.amount,
        vendorParticipationId: d.vendorParticipationId,
        vendorName: d.vendorName,
        vendorPaymentAccountId: d.vendorPaymentAccountId ?? null,
        vendorPaymentAccountName: d.vendorPaymentAccountName ?? null,
        paymentAccountName: d.vendorPaymentAccountName ?? null,
        vendorSuggestions: d.vendorSuggestions || [],
        description: d.description,
        memo: d.memo,
        paymentMethod: d.paymentMethod,
        disposition: d.disposition,
        matchingMode: d.matchingMode,
        originalText: d.originalText,
        provisioning: d.provisioning,
        accounting: d.accounting,
      })),
      // Preserve attorney fees with accounting info
      attorneysFees: fetchSettlement.attorneysFees
        ? {
            total: fetchSettlement.attorneysFees.total,
            feePrecentage: fetchSettlement.attorneysFees.feePrecentage,
            leadFirm: fetchSettlement.attorneysFees.leadFirm,
            secondaryFirms: fetchSettlement.attorneysFees.secondaryFirms || [],
          }
        : undefined,
      // Preserve net settlement with accounting info
      netSettlement: fetchSettlement.netSettlement,
      filename: fetchSettlement.filename || '',
      docpath: fetchSettlement.docpath || '',
      fileHash: fetchSettlement.fileHash || '',
      warningMessages: fetchSettlement.warningMessages || [],
      isAccountingIntegrated: fetchSettlement.isAccountingIntegrated || false,
    };

    try {
      const response = await saveDraft(draftData);
      if (response && response.data) {
        setCurrentDraftId(response.data.draftId);
        setAlert({
          message: 'Draft saved successfully.',
          type: 'success',
        });
        // Set initial state after first save (excluding accounting fields)
        setInitialDraftState(JSON.stringify(removeAccountingFields(fetchSettlement)));
        // Update URL to include draftId
        navigate(
          `/payments/settlement-draft?caseId=${fetchSettlement.case?.id}&draftId=${response.data.draftId}`,
          {
            replace: true,
          },
        );
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert({
        message: handleDraftError(axiosError),
        type: 'error',
      });
    }
  };

  const handleUpdateDraft = async () => {
    if (!fetchSettlement || !currentDraftId) return;

    // Validate required fields
    if (!fetchSettlement.case || !fetchSettlement.client || !fetchSettlement.defendant) {
      setAlert({
        message:
          'Missing required settlement information. Please ensure case, client, and defendant are properly set.',
        type: 'error',
      });
      return;
    }

    // Prepare draft data - preserve ALL fields for vendor matching, accounting sync, etc.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const draftData: any = {
      case: fetchSettlement.case,
      client: fetchSettlement.client,
      defendant: fetchSettlement.defendant,
      settledAmount: fetchSettlement.settledAmount || 0,
      pipAmount: fetchSettlement.pipAmount || 0,
      settlementType: fetchSettlement.settlementType || 'STANDARD',
      status: fetchSettlement.status || 'Draft',
      // Preserve complete deduction structure including vendor matching state and accounting
      deductions: (fetchSettlement.deductions || []).map((d) => ({
        id: d.id,
        type: d.type,
        amount: d.amount,
        vendorParticipationId: d.vendorParticipationId,
        vendorName: d.vendorName,
        vendorPaymentAccountId: d.vendorPaymentAccountId ?? null,
        vendorPaymentAccountName: d.vendorPaymentAccountName ?? null,
        paymentAccountName: d.vendorPaymentAccountName ?? null,
        vendorSuggestions: d.vendorSuggestions || [],
        description: d.description,
        memo: d.memo,
        paymentMethod: d.paymentMethod,
        disposition: d.disposition,
        matchingMode: d.matchingMode,
        originalText: d.originalText,
        provisioning: d.provisioning,
        accounting: d.accounting,
      })),
      // Preserve attorney fees with accounting info
      attorneysFees: fetchSettlement.attorneysFees
        ? {
            total: fetchSettlement.attorneysFees.total,
            feePrecentage: fetchSettlement.attorneysFees.feePrecentage,
            leadFirm: fetchSettlement.attorneysFees.leadFirm,
            secondaryFirms: fetchSettlement.attorneysFees.secondaryFirms || [],
          }
        : undefined,
      // Preserve net settlement with accounting info
      netSettlement: fetchSettlement.netSettlement,
      filename: fetchSettlement.filename || '',
      docpath: fetchSettlement.docpath || '',
      fileHash: fetchSettlement.fileHash || '',
      warningMessages: fetchSettlement.warningMessages || [],
      isAccountingIntegrated: fetchSettlement.isAccountingIntegrated || false,
    };

    try {
      const response = await updateDraft(currentDraftId, draftData);
      if (response && response.data) {
        setAlert({
          message: 'Draft updated successfully.',
          type: 'success',
        });
        // Reset initial state after successful update (excluding accounting fields)
        setInitialDraftState(JSON.stringify(removeAccountingFields(fetchSettlement)));
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert({
        message: handleDraftError(axiosError),
        type: 'error',
      });
    }
  };

  const handleDeleteDraft = async () => {
    if (!currentDraftId) return;

    try {
      await deleteDraft(currentDraftId);
      setAlert({
        message: 'Draft deleted successfully.',
        type: 'success',
      });
      handleCloseDeleteDraftPopup();
      // Navigate back to drafts list or create settlement page
      navigate('/payments/drafts');
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert({
        message: handleDraftError(axiosError),
        type: 'error',
      });
      handleCloseDeleteDraftPopup();
    }
  };

  return (
    <>
      {isLoading ? (
        <Box position="absolute" top="50%" left="50%">
          <Loader colorType="warning" />
        </Box>
      ) : !fetchSettlement && !isLoading ? (
        <Box className={classes.noDetailsTextBlock}>
          <Typography className={classes.noDetailsText}>
            No draft data is currently available.
            <br />
            The draft page will be displayed once a settlement is created using memo upload.
          </Typography>
          <Box className={classes.buttonWrapper}>
            <CommonButton
              type="infoMain"
              buttonText="Go to Create Settlement"
              onButtonClick={() => navigate('/payments/create-settlement')}
            />
          </Box>
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="flex-start" alignItems="center" gap="8px" mb={2}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: 'Inter',
                color: '#ffffff',
                ml: '14px',
              }}
            >
              {fetchSettlement?.case?.name || ''}
            </Typography>
            {fetchSettlement?.case?.id && canViewCases && (
              <FormLinkButton path={`/cases/${fetchSettlement.case.id}/home`} />
            )}
          </Box>

          <Box className={classes.pageContainer}>
            <Box className={classes.formContainer}>
              {(fetchSettlement?.warningMessages?.length ?? 0) > 0 && (
                <Box className={classes.warningBox}>
                  <Box className={classes.warningIconWrapper}>
                    <InfoOutlinedIcon sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography className={classes.warningHeading}>Please Review</Typography>
                    {fetchSettlement?.warningMessages?.map((message, index) => (
                      <Typography className={classes.warningMessage} key={`${index}-${message}`}>
                        {message}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              {(hasFuzzyOrNullMatchingMode || hasVendorProvisioningIssue) && (
                <>
                  <Box className={classes.warningBox}>
                    <Box className={classes.warningIconWrapper}>
                      <ErrorIcon sx={{ fontSize: 40 }} />
                    </Box>
                    <Box>
                      <Typography className={classes.warningHeading}>
                        Validation Required
                      </Typography>
                      {hasFuzzyOrNullMatchingMode && (
                        <Typography className={classes.warningMessage}>
                          Some vendors need confirmation or accounting entities need syncing. You can
                          save this as a draft and complete these validations later, but you must
                          resolve all issues before creating the final settlement.
                        </Typography>
                      )}
                      {hasVendorProvisioningIssue && (
                        <Typography className={classes.warningMessage}>
                          Some vendors are missing a payment account/address or have a payment
                          account/address that is not fully provisioned. You can save this as a
                          draft and complete these validations later, but you must resolve all
                          issues before creating the final settlement.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </>
              )}
              <Box className={classes.topRowContainer} mt={1}>
                <Box className={classes.leftColumnsWrapper}>
                  <Box className={classes.formLeftColumnContainer}>
                    <Box className={classes.formCheckDetailsContainer}>
                      <Box className={classes.settlementSummaryBox}>
                        <Typography className={classes.feesSummaryHeadline}>
                          Settlement Preview
                        </Typography>

                        <Box className={classes.settlementSummaryRow}>
                          <Typography className={classes.settlementSummaryKeyGross}>
                            Settled Amount
                          </Typography>
                          <Box className={classes.settlementSummaryAmountActions}>
                            {(fetchSettlement?.settlementType === 'STANDARD_PIP' ||
                              Boolean(fetchSettlement?.pipAmount)) && (
                              <StatusChip
                                label={`PIP ${numberToUSDCurrency(fetchSettlement?.pipAmount ?? 0)}`}
                                color={theme.palette.success.main}
                              />
                            )}
                            <Typography
                              className={`${classes.settlementSummaryValue} ${classes.settlementSummaryAmount}`}
                            >
                              {fetchSettlement?.settledAmount
                                ? numberToUSDCurrency(fetchSettlement.settledAmount)
                                : '$0.00'}
                            </Typography>
                            <IconButton
                              aria-label="Edit settlement and PIP amounts"
                              size="small"
                              onClick={() => setIsEditAmountsPopupOpen(true)}
                              sx={{ color: theme.palette.info.light }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Box className={classes.settlementSummaryRow}>
                          <Typography className={classes.settlementSummaryKeyDeduction}>
                            Attorney’s Fees
                          </Typography>
                          <Typography className={classes.settlementSummaryValue}>
                            {numberToUSDCurrency(calculateAttorneysFees())}
                          </Typography>
                        </Box>
                        <Box className={classes.settlementSummaryRow}>
                          <Typography className={classes.settlementSummaryKeyDeduction}>
                            Deductions
                          </Typography>
                          <Typography className={classes.settlementSummaryValue}>
                            {numberToUSDCurrency(calculateTotalDeductions())}
                          </Typography>
                        </Box>
                        <Box className={classes.settlementSummaryRow}>
                          <Typography className={classes.settlementSummaryKeyIndented}>
                            Liens
                          </Typography>
                          <Typography className={classes.settlementSummaryValueIndented}>
                            {numberToUSDCurrency(calculateLiens())}
                          </Typography>
                        </Box>
                        <Box className={classes.settlementSummaryRow}>
                          <Typography className={classes.settlementSummaryKeyIndented}>
                            Expenses
                          </Typography>
                          <Typography className={classes.settlementSummaryValueIndented}>
                            {numberToUSDCurrency(calculateExpenses())}
                          </Typography>
                        </Box>
                        <Box className={classes.settlementSummaryRowNet}>
                          <Typography className={classes.settlementSummaryKeyBold}>
                            Net To Plaintiff
                          </Typography>
                          <Box className={classes.settlementSummaryNetDetails}>
                            <Typography
                              className={`${classes.settlementSummaryValue} ${classes.settlementSummaryAmountValue}`}
                            >
                              {numberToUSDCurrency(calculateNetToPlaintiff())}
                            </Typography>
                            {(fetchSettlement?.pipAmount ?? 0) > 0 && (
                              <Typography className={classes.settlementSummaryNetPip}>
                                {`Including PIP amount ${numberToUSDCurrency(fetchSettlement?.pipAmount ?? 0)}`}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box className={classes.formActionsContainer}>
                  <FieldHeadline>Actions</FieldHeadline>
                  <Box mt="16px">
                    <CommonButton
                      disabled={hasFuzzyOrNullMatchingMode || hasVendorProvisioningIssue}
                      type="infoMain"
                      buttonText={'Save Settlement'}
                      onButtonClick={onSubmitForm}
                    />
                  </Box>
                  {currentDraftId ? (
                    <>
                      <Box mt="16px">
                        <CommonButton
                          disabled={false}
                          type="error"
                          buttonText="Delete Draft"
                          onButtonClick={handleOpenDeleteDraftPopup}
                        />
                      </Box>
                    </>
                  ) : (
                    <Box mt="16px">
                      <CommonButton
                        disabled={false}
                        type="infoDark"
                        buttonText="Save as Draft"
                        onButtonClick={handleSaveDraft}
                      />
                    </Box>
                  )}
                  {!currentDraftId && (
                    <Box mt="16px">
                      <CommonButton
                        disabled={false}
                        type="error"
                        buttonText="Delete Document"
                        onButtonClick={handleOpenDeletePopup}
                      />
                    </Box>
                  )}
                  <Box mt="16px">
                    <CommonButton
                      disabled={false}
                      type="infoMain"
                      buttonText="View Document"
                      onButtonClick={handleDownloadFiles}
                    />
                  </Box>
                </Box>
              </Box>
              <Box className={classes.formLeftDeductionContainer}>
                <DraftPlaintiffTable
                  netSettlement={fetchSettlement?.netSettlement}
                  clientId={fetchSettlement?.client?.id}
                  onUpdate={handlePlaintiffUpdate}
                />
              </Box>

              <Box className={classes.formLeftDeductionContainer}>
                <DraftAttorneyFeeTable
                  fields={mappedAttorneysFirms}
                  handleMenuOptionClick={handleAttorneyFeeMenuOptionClick}
                  attorneyFeesTotal={calculateAttorneysFees()}
                  onFieldsUpdate={handleAttorneyFeeFieldUpdate}
                />
              </Box>

              <Box className={classes.formLeftDeductionContainer}>
                <DraftDeductionsTable
                  disabled={false}
                  handleMenuOptionClick={handleDeductionMenuOptionClick}
                  fields={deductionFields}
                  handleConfirmClick={handleConfirmClick}
                  deductionTotal={{
                    total: calculateTotalDeductions(),
                    liens: calculateLiens(),
                    liensWithheld: 0,
                    advances: calculateExpenses(),
                    advancesWithheld: 0,
                    serviceProviders: calculateServiceProviders(),
                    serviceProvidersWithheld: 0,
                  }}
                />
              </Box>
            </Box>

            <EditDraftAttorneyFeePopup
              isOpen={isEditAttorneyFeePopupOpen}
              loading={false}
              onCancel={() => {
                setIsEditAttorneyFeePopupOpen(false);
                setSelectedAttorneyFeeIndex(null);
              }}
              onSuccess={() => {
                setIsEditAttorneyFeePopupOpen(false);
                setSelectedAttorneyFeeIndex(null);
                // refreshSettlement();
              }}
              initialData={
                selectedAttorneyFeeIndex !== null && mappedAttorneysFirms.length > 0
                  ? mappedAttorneysFirms.find((firm) => firm.id === selectedAttorneyFeeIndex)
                  : undefined
              }
              settlementId={settlementId ? Number(settlementId) : 0}
              updatedData={getAttorneyFeeUpdatedData}
            />
            <EditDraftSettlementAmountsPopup
              isOpen={isEditAmountsPopupOpen}
              onClose={() => setIsEditAmountsPopupOpen(false)}
              onApply={handleApplyDraftAmounts}
              showPipAmount={fetchSettlement?.settlementType === 'STANDARD_PIP'}
              initialSettlementAmount={fetchSettlement?.settledAmount || 0}
              initialPipAmount={fetchSettlement?.pipAmount || 0}
            />

            <EditDraftDeductionPopup
              isOpen={isEditDeductionPopupOpen}
              loading={false}
              canViewVendors={canViewVendors}
              canCreateVendors={canCreateVendors}
              onCancel={() => {
                setIsEditDeductionPopupOpen(false);
                setSelectedDeductionIndex(null);
              }}
              onSuccess={() => {
                setIsEditDeductionPopupOpen(false);
                setSelectedDeductionIndex(null);
                // Auto-save will trigger via useEffect
              }}
              initialData={
                selectedDeductionIndex !== null && mappedDeductions.length > 0
                  ? mappedDeductions.find((deduction) => deduction.id === selectedDeductionIndex)
                  : undefined
              }
              settlementId={settlementId ? Number(settlementId) : 0}
              updatedData={getDeductionUpdatedData}
              isAccountingIntegrated={fetchSettlement?.isAccountingIntegrated}
            />

            <Popup
              isOpen={isDeletePopupOpen}
              headlineText={POPUP_DESCRIPTIONS.settlementDraftDelete.headlineText}
              contentText={POPUP_DESCRIPTIONS.settlementDraftDelete.contentText}
              onMainButtonClick={handleDeleteDocument}
              onSecondaryButtonClick={handleCloseDeletePopup}
            />

            <Popup
              isOpen={isDeleteDraftPopupOpen}
              headlineText="Delete Draft"
              contentText="Are you sure you want to delete this settlement memo draft? This action cannot be undone."
              onMainButtonClick={handleDeleteDraft}
              onSecondaryButtonClick={handleCloseDeleteDraftPopup}
            />

            <Popup
              isOpen={isDeleteDeductionPopupOpen}
              headlineText="Delete Deduction"
              contentText="Are you sure you want to delete this deduction? This action cannot be undone."
              onMainButtonClick={handleDeleteDeduction}
              onSecondaryButtonClick={handleCloseDeleteDeductionPopup}
            />

            <Popup
              isOpen={isSaveSettlementPopupOpen}
              headlineText="Save Settlement"
              contentText="This will create a final settlement and remove the draft. Do you want to continue?"
              onMainButtonClick={createSettlement}
              onSecondaryButtonClick={handleCloseSaveSettlementPopup}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default SettlementDraftView;
